import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithPopup,
  getRedirectResult,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  sendEmailVerification,
  verifyBeforeUpdateEmail,
  deleteUser,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  User as FirebaseUser,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  writeBatch,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { UserProfile } from '../types';

const googleProvider = new GoogleAuthProvider();
const AUTH_ACTION_URL = 'https://kodacare.pet/auth/action';

const REMEMBER_ME_KEY = 'kodacare_auth_expiry';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

// ── Seed medications written to Firestore on first registration ───────────────
const SEED_MEDICATIONS = [
  {
    name: '[Example Medication]',
    dosage_mg: '0mg',
    color_hex: '#90CAF9',
    shape_type: 'round1' as const,
    timeslots: ['08:00', '20:00'],
    reminders_enabled: false,
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

async function fetchUserProfile(uid: string): Promise<UserProfile | null> {
  // Try up to 2 times with a 15-second timeout each, to handle slow mobile
  // connections without leaving user === null and breaking the whole app.
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const timeout = new Promise<null>(resolve => setTimeout(() => resolve(null), 15_000));
      const snap = await Promise.race([
        getDoc(doc(db, 'users', uid)),
        timeout as Promise<never>,
      ]) as Awaited<ReturnType<typeof getDoc>> | null;
      if (!snap) continue; // timed out — retry
      if (!snap.exists()) return null;
      const data = snap.data();
      if (!data || typeof data !== 'object') return null;
      return { id: uid, ...(data as Record<string, unknown>) } as UserProfile;
    } catch {
      // network error — retry
    }
  }
  return null;
}

async function seedMedications(uid: string) {
  const batch = writeBatch(db);
  for (const med of SEED_MEDICATIONS) {
    const ref = doc(collection(db, 'users', uid, 'medications'));
    batch.set(ref, { ...med, user_id: uid });
  }
  await batch.commit();
}

async function deleteAllUserData(uid: string) {
  const batch = writeBatch(db);

  // Walk each date container and delete its entries subcollection
  const logDatesSnap = await getDocs(collection(db, 'users', uid, 'logs'));
  for (const dateDoc of logDatesSnap.docs) {
    const entriesSnap = await getDocs(
      collection(db, 'users', uid, 'logs', dateDoc.id, 'entries')
    );
    entriesSnap.forEach(d => batch.delete(d.ref));
    batch.delete(dateDoc.ref);
  }

  // Delete medications and contacts
  for (const col of ['medications', 'contacts']) {
    const snap = await getDocs(collection(db, 'users', uid, col));
    snap.forEach(d => batch.delete(d.ref));
  }

  // Delete public leaderboard (Companions) entries — stored outside the user
  // document tree at public_companions/{uid}_{petId}, so they are not caught
  // by the user-doc delete above.
  const companionsSnap = await getDocs(
    query(collection(db, 'public_companions'), where('user_id', '==', uid))
  );
  companionsSnap.forEach(d => batch.delete(d.ref));

  batch.delete(doc(db, 'users', uid));
  await batch.commit();
}

// ── Context types ─────────────────────────────────────────────────────────────

interface AuthContextType {
  user: UserProfile | null;
  firebaseUser: FirebaseUser | null;
  authLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  register: (
    email: string,
    password: string,
    fullName: string,
    transplantDate?: string
  ) => Promise<string>;
  logout: (options?: { redirectTo?: string | null }) => Promise<void>;
  updateUser: (updates: Partial<UserProfile>) => Promise<void>;
  sendPasswordReset: () => Promise<void>;
  requestEmailChange: (newEmail: string) => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ── Provider ──────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  // On first load, complete any pending signInWithRedirect flow.
  // onAuthStateChanged will fire automatically after this resolves.
  useEffect(() => {
    getRedirectResult(auth).catch(err => {
      console.error('[AuthContext] getRedirectResult error:', err);
    });
  }, []);

  // Single source of truth: Firebase Auth state.
  // setAuthLoading(true) is called at the top of every invocation so that any
  // auth state change (login, logout, token refresh) blocks the UI until the
  // Firestore profile fetch also completes — preventing ProtectedRoute from
  // running with a stale / null user.
  useEffect(() => {
    let profileUnsub: (() => void) | null = null;

    const unsubscribe = onAuthStateChanged(auth, async fbUser => {
      // Clean up any previous profile listener
      profileUnsub?.();
      profileUnsub = null;

      setAuthLoading(true);
      try {
        if (fbUser) {
          // Enforce 30-day expiry if it was set
          const expiry = localStorage.getItem(REMEMBER_ME_KEY);
          if (expiry && Date.now() > Number(expiry)) {
            localStorage.removeItem(REMEMBER_ME_KEY);
            await signOut(auth);
            return;
          }
          setFirebaseUser(fbUser);

          // Set up a real-time listener on the user profile document so that
          // changes made from any session (app or website) are reflected immediately.
          let initialLoad = true;
          profileUnsub = onSnapshot(doc(db, 'users', fbUser.uid), snap => {
            if (snap.exists()) {
              const profile = snap.data() as Record<string, unknown>;
              const authEmail = fbUser.email || '';
              const profileEmail = typeof profile.email === 'string' ? profile.email : '';

              // Keep Firestore profile email in sync with Firebase Auth email
              // (e.g. after verifyBeforeUpdateEmail flow completes).
              if (authEmail && profileEmail !== authEmail) {
                void setDoc(doc(db, 'users', fbUser.uid), { email: authEmail }, { merge: true })
                  .catch(err => console.error('[AuthContext] Failed to sync profile email:', err));
              }

              setUser({ id: fbUser.uid, ...profile, email: authEmail || profileEmail } as UserProfile);
            } else if (initialLoad) {
              // Profile not yet written (register() still in flight) — synthesise a minimal one
              setUser({
                id: fbUser.uid,
                role: 'patient',
                full_name: fbUser.displayName || fbUser.email?.split('@')[0] || 'KodaCare User',
                email: fbUser.email || '',
              });
            }
            if (initialLoad) {
              initialLoad = false;
              setAuthLoading(false);
            }
          }, err => {
            console.error('[AuthContext] profile snapshot error:', err);
            if (initialLoad) {
              setUser(null);
              setAuthLoading(false);
              initialLoad = false;
            }
          });

          // Don't call setAuthLoading(false) here — the snapshot callback handles it
          return;
        } else {
          setFirebaseUser(null);
          setUser(null);
        }
      } catch (err) {
        console.error('[AuthContext] onAuthStateChanged error:', err);
        setFirebaseUser(null);
        setUser(null);
      } finally {
        // Only mark loading done here for the signed-out path
        if (!fbUser) setAuthLoading(false);
      }
    });

    return () => {
      unsubscribe();
      profileUnsub?.();
    };
  }, []);

  const login = useCallback(async (email: string, password: string, rememberMe = true) => {
    // Installed PWA (standalone) always uses LOCAL persistence — closing the app
    // kills the WebView process, which clears sessionStorage and signs the user out.
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || (navigator as Navigator & { standalone?: boolean }).standalone === true;
    const persistence = (rememberMe || isStandalone) ? browserLocalPersistence : browserSessionPersistence;
    await setPersistence(auth, persistence);
    await signInWithEmailAndPassword(auth, email, password);
    if (rememberMe) {
      localStorage.setItem(REMEMBER_ME_KEY, String(Date.now() + THIRTY_DAYS_MS));
    } else {
      localStorage.removeItem(REMEMBER_ME_KEY);
    }
    // onAuthStateChanged handles state update for existing users
  }, []);

  const loginWithGoogle = useCallback(async () => {
    await setPersistence(auth, browserLocalPersistence);
    localStorage.setItem(REMEMBER_ME_KEY, String(Date.now() + THIRTY_DAYS_MS));

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const fbUser = result.user;
      const existing = await fetchUserProfile(fbUser.uid);
      if (!existing) {
        const profileData = {
          role: 'patient' as const,
          full_name: fbUser.displayName || 'KodaCare User',
          email: fbUser.email || '',
        };
        await setDoc(doc(db, 'users', fbUser.uid), profileData);
        await seedMedications(fbUser.uid);
        setFirebaseUser(fbUser);
        setUser({ id: fbUser.uid, ...profileData });
      }
      // Returning user: onAuthStateChanged already set state
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      // Only rethrow if it's not a user-dismissed popup
      if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
        throw err;
      }
    }
  }, []);

  const register = useCallback(async (
    email: string,
    password: string,
    fullName: string,
    transplantDate?: string
  ) => {
    // 1. Create the Firebase Auth account
    const { user: fbUser } = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );

    // 2. Write Firestore profile. If this fails, roll back the auth account
    //    so the user isn't left with auth-but-no-data.
    try {
      const profileData = {
        role: 'patient' as const,
        full_name: fullName,
        email,
        ...(transplantDate ? { transplant_date: transplantDate } : {}),
      };
      await setDoc(doc(db, 'users', fbUser.uid), profileData);
      await seedMedications(fbUser.uid);
      try {
        await sendEmailVerification(fbUser, {
          url: AUTH_ACTION_URL,
          handleCodeInApp: false,
        });
      } catch (verificationError) {
        console.error('[AuthContext] Email verification send failed:', verificationError);
      }

      // 3. Set context state directly — onAuthStateChanged fired while the doc
      //    was being written and found nothing; we correct it here.
      //    Also clear authLoading (onAuthStateChanged returned early keeping
      //    it true, so we own the responsibility of clearing it).
      setFirebaseUser(fbUser);
      setUser({ id: fbUser.uid, ...profileData });
      setAuthLoading(false);
    } catch (firestoreError) {
      // Roll back: delete the auth account so the user can try again cleanly
      console.error('[AuthContext] Firestore write failed after account creation — rolling back auth account:', firestoreError);
      await deleteUser(fbUser);
      throw firestoreError;
    }

    return fbUser.uid;
  }, []);

  const logout = useCallback(async (options?: { redirectTo?: string | null }) => {
    localStorage.removeItem(REMEMBER_ME_KEY);
    
    // Clear notification and reminder cache keys so they don't bleed across accounts
    localStorage.removeItem('koda_notifications');
    localStorage.removeItem('koda_fired_med_times');
    // Also remove any dated last_log_reminder keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('koda_last_log_reminder_')) {
        localStorage.removeItem(key);
      }
    }

    await signOut(auth);
    if (options?.redirectTo !== null) {
      window.location.href = options?.redirectTo ?? '/website';
    }
  }, []);

  const updateUser = useCallback(async (updates: Partial<UserProfile>) => {
    if (!firebaseUser) return;
    // Strip the local 'id' field — Firestore doc path is already the uid
    const { id: _id, ...withId } = updates as UserProfile;
    // Firestore rejects undefined values — remove them before writing
    const firestoreUpdates = Object.fromEntries(
      Object.entries(withId).filter(([, v]) => v !== undefined)
    );
    try {
      await setDoc(doc(db, 'users', firebaseUser.uid), firestoreUpdates, { merge: true });
      setUser(prev => (prev ? { ...prev, ...updates } : null));
    } catch (err) {
      console.error('[AuthContext] updateUser failed:', err);
      throw err;
    }
  }, [firebaseUser]);

  const sendPasswordReset = useCallback(async () => {
    if (!firebaseUser?.email) return;
    await sendPasswordResetEmail(auth, firebaseUser.email, {
      url: AUTH_ACTION_URL,
      handleCodeInApp: false,
    });
  }, [firebaseUser]);

  const requestEmailChange = useCallback(async (newEmail: string) => {
    if (!firebaseUser) throw new Error('Not signed in');
    await verifyBeforeUpdateEmail(firebaseUser, newEmail, {
      url: AUTH_ACTION_URL,
      handleCodeInApp: false,
    });
  }, [firebaseUser]);

  const resendVerificationEmail = useCallback(async () => {
    if (!firebaseUser) throw new Error('Not signed in');
    await sendEmailVerification(firebaseUser, {
      url: AUTH_ACTION_URL,
      handleCodeInApp: false,
    });
  }, [firebaseUser]);

  const deleteAccount = useCallback(async () => {
    if (!firebaseUser) return;
    await deleteAllUserData(firebaseUser.uid);
    await deleteUser(firebaseUser);
    // onAuthStateChanged fires with null → user + firebaseUser cleared
  }, [firebaseUser]);

  const contextValue = useMemo(() => ({
    user,
    firebaseUser,
    authLoading,
    isAuthenticated: !!firebaseUser,
    login,
    loginWithGoogle,
    register,
    logout,
    updateUser,
    sendPasswordReset,
    requestEmailChange,
    resendVerificationEmail,
    deleteAccount,
  }), [user, firebaseUser, authLoading, login, loginWithGoogle, register, logout, updateUser, sendPasswordReset, requestEmailChange, resendVerificationEmail, deleteAccount]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
